import Dexie, { Table } from 'dexie'

// ============================================
// TYPES FOR LOCAL DATABASE
// ============================================

export type SyncStatus = 'pending' | 'synced' | 'conflict' | 'error'

export interface LocalUser {
  id: string
  email: string
  display_name?: string
  unit_system: 'imperial' | 'metric'
  current_phase: 1 | 2
  current_week: number
  volume_tier: 1 | 2 | 3
  trt_dose_mg?: number
  updated_at: string
}

export interface LocalBodyWeightLog {
  id: string
  user_id: string
  date: string // ISO date string YYYY-MM-DD
  weight_kg: number
  body_fat_pct?: number
  notes?: string
  sync_status: SyncStatus
  created_at: string
  updated_at: string
}

export interface LocalWorkoutSession {
  id: string
  user_id: string
  date: string // ISO datetime string
  workout_type: string
  phase: 1 | 2
  week: number
  volume_tier: 1 | 2 | 3
  is_deload: boolean
  total_volume_kg?: number
  duration_minutes?: number
  notes?: string
  completed: boolean
  sync_status: SyncStatus
  created_at: string
  updated_at: string
}

export interface LocalExerciseLog {
  id: string
  session_id: string
  exercise_id: string
  slot_identifier?: string
  order_index: number
  notes?: string
  sync_status: SyncStatus
  created_at: string
}

export interface LocalSetLog {
  id: string
  exercise_log_id: string
  set_number: number
  weight_kg: number
  reps: number
  rir?: number
  set_type: 'working' | 'warmup' | 'rest_pause' | 'drop'
  is_pr: boolean
  notes?: string
  sync_status: SyncStatus
  created_at: string
}

export interface LocalCycle {
  id: string
  user_id: string
  name: string
  start_date: string
  end_date?: string
  status: 'planning' | 'active' | 'completed'
  cycle_type: 'blast' | 'cruise' | 'pct'
  notes?: string
  sync_status: SyncStatus
  created_at: string
  updated_at: string
}

export interface LocalCycleCompound {
  id: string
  cycle_id: string
  compound_id: string
  dose_mg: number
  frequency: string
  start_week: number
  end_week: number
  notes?: string
  sync_status: SyncStatus
  created_at: string
}

export interface LocalInjectionLog {
  id: string
  user_id: string
  cycle_id?: string
  date: string
  compound_id: string
  dose_mg: number
  injection_site?: string
  oil_volume_ml?: number
  notes?: string
  sync_status: SyncStatus
  created_at: string
}

export interface LocalSideEffectLog {
  id: string
  user_id: string
  date: string
  symptom: string
  severity: number
  suspected_cause?: string
  notes?: string
  sync_status: SyncStatus
  created_at: string
}

export interface LocalBloodworkLog {
  id: string
  user_id: string
  date: string
  cycle_id?: string
  timing?: string
  results_json?: Record<string, unknown>
  overall_status: 'normal' | 'flagged' | 'critical'
  notes?: string
  sync_status: SyncStatus
  created_at: string
}

// Active workout state for resume functionality
export interface LocalActiveWorkoutState {
  id: string // Always 'current' - singleton record
  user_id: string
  session_id: string
  workout_type: string
  phase: 1 | 2
  week: number
  volume_tier: 1 | 2 | 3
  is_deload: boolean
  start_time: number // Date.now() when workout started
  current_exercise_index: number
  exercises_json: string // JSON stringified ActiveExercise[]
  rest_timer_end?: number // Unix timestamp for active rest timer
  last_saved_at: string // ISO timestamp
}

// Custom exercises created by user
export interface LocalCustomExercise {
  id: string
  user_id: string
  name: string
  muscle_group: string
  movement_bias?: string
  equipment?: string[]
  is_compound: boolean
  notes?: string
  sync_status: SyncStatus
  created_at: string
}

// Sync queue for tracking operations
export interface SyncQueueItem {
  id: string
  table_name: string
  record_id: string
  operation: 'insert' | 'update' | 'delete'
  data?: Record<string, unknown>
  created_at: string
  retry_count: number
  last_error?: string
}

// ============================================
// DEXIE DATABASE CLASS
// ============================================

class XFrameDB extends Dexie {
  // Tables
  users!: Table<LocalUser>
  bodyWeightLogs!: Table<LocalBodyWeightLog>
  workoutSessions!: Table<LocalWorkoutSession>
  exerciseLogs!: Table<LocalExerciseLog>
  setLogs!: Table<LocalSetLog>
  cycles!: Table<LocalCycle>
  cycleCompounds!: Table<LocalCycleCompound>
  injectionLogs!: Table<LocalInjectionLog>
  sideEffectLogs!: Table<LocalSideEffectLog>
  bloodworkLogs!: Table<LocalBloodworkLog>
  syncQueue!: Table<SyncQueueItem>
  activeWorkoutState!: Table<LocalActiveWorkoutState>
  customExercises!: Table<LocalCustomExercise>

  constructor() {
    super('xframe-companion')

    this.version(1).stores({
      // Indexed fields for each table
      users: 'id, email',
      bodyWeightLogs: 'id, user_id, date, sync_status, [user_id+date]',
      workoutSessions: 'id, user_id, date, workout_type, sync_status, [user_id+date]',
      exerciseLogs: 'id, session_id, exercise_id, sync_status',
      setLogs: 'id, exercise_log_id, sync_status',
      cycles: 'id, user_id, status, sync_status',
      cycleCompounds: 'id, cycle_id, compound_id, sync_status',
      injectionLogs: 'id, user_id, date, cycle_id, sync_status, [user_id+date]',
      sideEffectLogs: 'id, user_id, date, sync_status, [user_id+date]',
      bloodworkLogs: 'id, user_id, date, cycle_id, sync_status',
      syncQueue: 'id, table_name, record_id, operation, created_at',
    })

    // Version 2: Add active workout state and custom exercises tables
    this.version(2).stores({
      users: 'id, email',
      bodyWeightLogs: 'id, user_id, date, sync_status, [user_id+date]',
      workoutSessions: 'id, user_id, date, workout_type, sync_status, [user_id+date]',
      exerciseLogs: 'id, session_id, exercise_id, sync_status',
      setLogs: 'id, exercise_log_id, sync_status',
      cycles: 'id, user_id, status, sync_status',
      cycleCompounds: 'id, cycle_id, compound_id, sync_status',
      injectionLogs: 'id, user_id, date, cycle_id, sync_status, [user_id+date]',
      sideEffectLogs: 'id, user_id, date, sync_status, [user_id+date]',
      bloodworkLogs: 'id, user_id, date, cycle_id, sync_status',
      syncQueue: 'id, table_name, record_id, operation, created_at',
      activeWorkoutState: 'id, user_id, session_id',
      customExercises: 'id, user_id, name, muscle_group, sync_status',
    })
  }
}

export const db = new XFrameDB()

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Generate a UUID v4 for client-side record creation
 */
export function generateId(): string {
  return crypto.randomUUID()
}

/**
 * Get current ISO timestamp
 */
export function nowISO(): string {
  return new Date().toISOString()
}

/**
 * Get current date as YYYY-MM-DD
 */
export function todayDate(): string {
  return new Date().toISOString().split('T')[0]
}

/**
 * Add item to sync queue
 */
export async function queueSync(
  tableName: string,
  recordId: string,
  operation: 'insert' | 'update' | 'delete',
  data?: Record<string, unknown>
): Promise<void> {
  await db.syncQueue.add({
    id: generateId(),
    table_name: tableName,
    record_id: recordId,
    operation,
    data,
    created_at: nowISO(),
    retry_count: 0,
  })
}

/**
 * Get all pending sync items
 */
export async function getPendingSyncItems(): Promise<SyncQueueItem[]> {
  return db.syncQueue.toArray()
}

/**
 * Remove item from sync queue after successful sync
 */
export async function removeSyncItem(id: string): Promise<void> {
  await db.syncQueue.delete(id)
}

/**
 * Mark sync item as failed and increment retry count
 */
export async function markSyncFailed(id: string, error: string): Promise<void> {
  const item = await db.syncQueue.get(id)
  if (item) {
    await db.syncQueue.update(id, {
      retry_count: item.retry_count + 1,
      last_error: error,
    })
  }
}

// ============================================
// ACTIVE WORKOUT STATE HELPERS
// ============================================

const ACTIVE_WORKOUT_ID = 'current' // Singleton ID
const STALE_WORKOUT_HOURS = 24

/**
 * Save or update the active workout state
 */
export async function saveActiveWorkoutState(
  state: Omit<LocalActiveWorkoutState, 'id' | 'last_saved_at'>
): Promise<void> {
  await db.activeWorkoutState.put({
    ...state,
    id: ACTIVE_WORKOUT_ID,
    last_saved_at: nowISO(),
  })
}

/**
 * Get the active workout state if exists and not stale
 */
export async function getActiveWorkoutState(): Promise<LocalActiveWorkoutState | null> {
  const state = await db.activeWorkoutState.get(ACTIVE_WORKOUT_ID)
  if (!state) return null

  // Check if stale (older than 24 hours)
  const lastSaved = new Date(state.last_saved_at).getTime()
  const hoursAgo = (Date.now() - lastSaved) / (1000 * 60 * 60)

  if (hoursAgo > STALE_WORKOUT_HOURS) {
    // Auto-delete stale workout
    await clearActiveWorkoutState()
    return null
  }

  return state
}

/**
 * Clear the active workout state
 */
export async function clearActiveWorkoutState(): Promise<void> {
  await db.activeWorkoutState.delete(ACTIVE_WORKOUT_ID)
}

/**
 * Check if there's an active workout without returning the full state
 */
export async function hasActiveWorkout(): Promise<boolean> {
  const state = await getActiveWorkoutState()
  return state !== null
}

// ============================================
// CUSTOM EXERCISES HELPERS
// ============================================

/**
 * Create a new custom exercise
 */
export async function createCustomExercise(
  exercise: Omit<LocalCustomExercise, 'id' | 'sync_status' | 'created_at'>
): Promise<LocalCustomExercise> {
  const newExercise: LocalCustomExercise = {
    ...exercise,
    id: generateId(),
    sync_status: 'pending',
    created_at: nowISO(),
  }
  await db.customExercises.add(newExercise)
  return newExercise
}

/**
 * Get all custom exercises for a user
 */
export async function getCustomExercises(userId: string): Promise<LocalCustomExercise[]> {
  return db.customExercises.where('user_id').equals(userId).toArray()
}

/**
 * Search custom exercises by name
 */
export async function searchCustomExercises(
  userId: string,
  query: string
): Promise<LocalCustomExercise[]> {
  const exercises = await getCustomExercises(userId)
  const lowerQuery = query.toLowerCase()
  return exercises.filter((e) => e.name.toLowerCase().includes(lowerQuery))
}

/**
 * Delete a custom exercise
 */
export async function deleteCustomExercise(id: string): Promise<void> {
  await db.customExercises.delete(id)
}

/**
 * Check if a custom exercise name already exists for a user
 */
export async function customExerciseExists(userId: string, name: string): Promise<boolean> {
  const exercises = await getCustomExercises(userId)
  const lowerName = name.toLowerCase().trim()
  return exercises.some((e) => e.name.toLowerCase().trim() === lowerName)
}

export default db
